import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Select, {
  type FormatOptionLabelMeta,
  type GroupBase,
  type SelectComponentsConfig
} from '@databases/atlaskit/Select';
import { SelectColorPicker } from '@databases/color-picker';
import { messages } from '@databases/intl/messages';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import {
  DatabaseFieldType,
  type SelectOption,
  type DatabaseField,
  type RawDatabaseValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type MultiChange,
  MultiValueChangeType,
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { pickNewColor } from '@databases/utils/database/field-types/select';
import { generateId } from '@databases/utils/utils';
import React, { type FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  compactTagSelectStyleConfig,
  isRenderedInSidePanelStyling
} from '../popover-edit-common/index.ts';

const getOptionValue = (op: SelectOption) => op.id ?? op.label;
const getOptionLabel = (op: SelectOption) => op.label;
type InputActionMeta = { action?: string };

function hasOption(options: SelectOption[], option: SelectOption): boolean {
  return options.some((op) => (op.id ?? op.label) === (option.id ?? option.label));
}

const createOptionContainerStyles = xcss({
  alignItems: 'center',
  display: 'flex',
  gap: 'space.100',
  justifyContent: 'space-between',
  width: '100%'
});

const createOptionLabelStyles = xcss({
  minWidth: '0'
});

const createOptionColorPickerStyles = xcss({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0
});

export interface TagSelectProps {
  options: SelectOption[];
  value?: string[];
  isCreatable?: boolean;
  isMulti: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  preventOpenWhenNoOptions?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
  field: DatabaseField<DatabaseFieldType.SELECT>;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  addChangeToDraft?: (change: DatabaseChange) => void;
  onChange: (
    value: RawDatabaseValue<DatabaseFieldType.SELECT>,
    change: ValueChange | null
  ) => void;
  isRenderedInSidePanel?: boolean;
  isRenderedInContextualFieldMenu?: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  initialKey?: string;
}

const TagSelect: FC<TagSelectProps> = ({
  options,
  value = Object.freeze([]) as never[],
  isCreatable = true,
  isMulti,
  isInvalid = false,
  autoFocus,
  placeholder,
  appearance,
  field,
  onFocus,
  onBlur,
  addChangeToDraft,
  onChange,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  addDataSectionRef,
  initialKey
}) => {
  const [nextColor, setNextColor] = useState(pickNewColor(options));
  useEffect(() => {
    setNextColor(pickNewColor(options));
  }, [options]);

  const [inputValue, setInputValue] = useState(initialKey ?? '');
  const handleInputChange = useCallback((val: string, meta?: InputActionMeta) => {
    if (meta && meta.action !== 'input-change') {
      return;
    }
    setInputValue(val);
  }, []);

  const { formatMessage } = useIntl();

  const resolvedOptions = useMemo(() => {
    const result = [...options];
    if (isCreatable) {
      const trimmed = inputValue.trim();
      const trimmedLower = inputValue.trim().toLowerCase();
      if (trimmed && !options?.some((o) => o.label.toLowerCase() === trimmedLower)) {
        result.unshift({ id: generateId(), label: trimmed, color: nextColor });
      }
    }
    return result.filter((o) => o.label.trim().length > 0);
  }, [inputValue, isCreatable, nextColor, options]);

  const resolvedValue = useMemo(
    () => options.filter((op) => value.includes(op.id ?? op.label)),
    [options, value]
  );

  const handleCreate = useCallback(
    (option: SelectOption) => {
      const optionValue = option.id ?? option.label;
      if (!addChangeToDraft) {
        return;
      }

      addChangeToDraft({
        type: DatabaseChangeType.SET_SELECT_OPTIONS,
        fieldId: field.id,
        newValue: [...field.settings.selectOptions, option],
        change: {
          type: MultiValueChangeType.APPEND,
          value: option
        }
      });

      const tagValues = resolvedValue.map((v) => v.id ?? v.label);
      const nextValue = isMulti ? [...tagValues, optionValue] : [optionValue];
      const delta: MultiChange<DatabaseFieldType.SELECT> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: optionValue
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [optionValue]
          };

      onChange(nextValue, {
        type: DatabaseFieldType.SELECT,
        delta
      });
    },
    [
      addChangeToDraft,
      field.id,
      field.settings.selectOptions,
      isMulti,
      onChange,
      resolvedValue
    ]
  );

  const handleSelect = useCallback(
    (passedValue: string | null) => {
      if (!passedValue) {
        return;
      }
      const tagValues = resolvedValue.map((v) => v.id ?? v.label);

      const delta: MultiChange<DatabaseFieldType.SELECT> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: passedValue
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [passedValue]
          };

      const nextValue = isMulti ? [...tagValues, passedValue] : [passedValue];

      onChange(nextValue, {
        type: DatabaseFieldType.SELECT,
        delta
      });
    },
    [isMulti, onChange, resolvedValue]
  );

  const handleChange = useCallback(
    (selectedValue: SelectOption | readonly SelectOption[] | null) => {
      if (Array.isArray(selectedValue)) {
        setInputValue('');
        const createdOption = selectedValue.find((option) => !hasOption(options, option));
        if (createdOption) {
          handleCreate(createdOption);
          setInputValue('');
          return;
        }
        const selectedValues = selectedValue.map((option) => option.id ?? option.label);
        onChange(selectedValues, {
          type: DatabaseFieldType.SELECT,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: selectedValues
          }
        });
        return;
      }

      if (!selectedValue) {
        setInputValue('');
        onChange([], {
          type: DatabaseFieldType.SELECT,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: []
          }
        });
        return;
      }

      const selectedOption = selectedValue as SelectOption | null;
      if (selectedOption) {
        setInputValue('');
        if (!hasOption(options, selectedOption)) {
          handleCreate(selectedOption);
          setInputValue('');
          return;
        }
        handleSelect(selectedOption.id ?? selectedOption.label);
      }
    },
    [handleSelect, handleCreate, onChange, options]
  );

  const formatOptionLabel = useCallback(
    (option: SelectOption, meta: FormatOptionLabelMeta<SelectOption>) => {
      if (meta.context === 'menu' && !hasOption(options, option)) {
        return (
          <Box xcss={createOptionContainerStyles}>
            <Inline
              alignBlock="center"
              grow="fill"
              space="space.050"
              shouldWrap={false}
              xcss={createOptionLabelStyles}
            >
              <Text as="span">
                {formatMessage(messages['field-settings.select-picker-create-text'])}
              </Text>
              <SelectOptionTag option={option} noMargin />
            </Inline>
            <Box xcss={createOptionColorPickerStyles}>
              <SelectColorPicker
                value={option.color}
                onChange={(color) => {
                  setNextColor(color);
                }}
                stopPropagation
                size={24}
              />
            </Box>
          </Box>
        );
      } else if (meta.context === 'menu' || !isMulti) {
        return (
          <SelectOptionTag option={option} noMargin={isRenderedInContextualFieldMenu} />
        );
      } else {
        return option.label;
      }
    },
    [formatMessage, isMulti, isRenderedInContextualFieldMenu, options]
  );

  const components: SelectComponentsConfig<
    SelectOption,
    boolean,
    GroupBase<SelectOption>
  > = useMemo(
    () => ({
      MultiValue: ({ data, removeProps }) => (
        <SelectOptionTag
          option={data}
          isRemovable
          onRemove={removeProps.onClick}
          noMargin
        />
      )
    }),
    []
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel && !isRenderedInContextualFieldMenu) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInContextualFieldMenu, isRenderedInSidePanel]
  );

  return (
    <Select<SelectOption, boolean>
      classNamePrefix="select-option-picker"
      options={resolvedOptions}
      value={resolvedValue}
      onChange={handleChange}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      onBlur={handleBlur}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      placeholder={
        placeholder ??
        (isCreatable
          ? formatMessage(messages['select-picker-popover-edit.createable-placeholder'])
          : formatMessage(messages['select-picker-popover-edit.placeholder']))
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      isInvalid={isInvalid}
      onFocus={onFocus}
      autoFocus={autoFocus}
      formatOptionLabel={formatOptionLabel}
      styles={
        isRenderedInContextualFieldMenu
          ? compactTagSelectStyleConfig
          : isRenderedInSidePanel
            ? isRenderedInSidePanelStyling
            : baseSelectStyleConfig
      }
      appearance={appearance}
      hideSelectedOptions
      controlShouldRenderValue={Boolean(isRenderedInContextualFieldMenu)}
      spacing={isRenderedInContextualFieldMenu ? 'compact' : undefined}
      {...(isRenderedInContextualFieldMenu && {
        components,
        isClearable: value.length > 0,
        isMulti
      })}
      {...(!isRenderedInSidePanel &&
        !isRenderedInContextualFieldMenu && {
          menuPortalTarget: null,
          defaultMenuIsOpen: true,
          menuIsOpen: true,
          components: {
            IndicatorsContainer: () => null,
            ValueContainer: CustomValueContainer
          }
        })}
    />
  );
};

export default TagSelect;
